// ---------------------------------------------------------------------------
// Quantum's consumer of the backend canonical strategy-visibility projection.
//
// Quantum does not decide any of this. The backend already answered five
// SEPARATE questions — may this be read, is it the current candidate, may it
// be approved, published, released — and this module's only job is to read
// that answer without re-deriving it from hashes, campaign.status, legacy
// governance flags or operation status.
//
// Two rules govern everything here:
//
//   1. canViewStrategy is independent of every mutation verdict. A blocked
//      approval must never hide a legitimate persisted artifact from an
//      internal reviewer — that inversion is what left operators unable to
//      read the strategies they were being asked to fix.
//   2. Mutation controls FAIL CLOSED. A missing or malformed projection
//      disables approve/publish/release/generate. It never falls back to a
//      permissive local guess.
// ---------------------------------------------------------------------------

export const STRATEGY_ARTIFACT_STATUS = Object.freeze({
  CURRENT_REVIEWABLE: "CURRENT_REVIEWABLE",
  HISTORICAL_BOUND: "HISTORICAL_BOUND",
  HISTORICAL_UNBOUND: "HISTORICAL_UNBOUND",
  UNAVAILABLE: "UNAVAILABLE",
});

const KNOWN_STATUSES = new Set(Object.values(STRATEGY_ARTIFACT_STATUS));

export const STRATEGY_BLOCKING_REASON_LABELS = Object.freeze({
  ALLOCATION_REQUIRED: "ALLOCATION REQUIRED",
  INVALID_DEPENDENCY_MATERIAL: "INVALID DEPENDENCY MATERIAL",
  SUBSTANTIVE_DEPENDENCY_CHANGED: "SUBSTANTIVE DEPENDENCY CHANGED",
  MIXED_IDENTITY_GENERATION: "MIXED IDENTITY GENERATION",
  APPROVAL_REQUIRED: "APPROVAL REQUIRED",
  NO_PERSISTED_ARTIFACT: "NO PERSISTED STRATEGY",
  AUTHORIZATION_REQUIRED: "CLIENT AUTHORIZATION REQUIRED",
  BINDING_EVIDENCE_UNVERIFIABLE: "BINDING EVIDENCE UNVERIFIABLE",
  LEGACY_BINDING_UNVERIFIABLE: "BINDING EVIDENCE UNVERIFIABLE",
  CAMPAIGN_APPROVAL_STATE_CONTRADICTORY: "CAMPAIGN APPROVAL STATE UNRESOLVED",
});

/**
 * Plain-language sentences for the codes an operator can act on.
 *
 * The label above is a short chip; this is the actionable half. A code with no
 * entry here gets the generic sentence rather than its own identifier, because
 * an operator reading "CAMPAIGN_APPROVAL_STATE_CONTRADICTORY" on a page has
 * been handed a log line, not an instruction.
 */
export const STRATEGY_BLOCKING_REASON_DETAILS = Object.freeze({
  ALLOCATION_REQUIRED:
    "A governed channel allocation must be approved before this strategy can be reviewed.",
  INVALID_DEPENDENCY_MATERIAL:
    "Some of this campaign's governed inputs are incomplete, so the strategy cannot be checked against them.",
  SUBSTANTIVE_DEPENDENCY_CHANGED:
    "The campaign's governed inputs changed after this strategy was generated, so it no longer describes the current campaign.",
  MIXED_IDENTITY_GENERATION:
    "This strategy was assembled from more than one campaign identity and cannot be approved as current.",
  APPROVAL_REQUIRED: "This strategy is ready for review and needs your approval.",
  NO_PERSISTED_ARTIFACT: "No strategy has been generated for this campaign yet.",
  AUTHORIZATION_REQUIRED: "The advertiser must authorize this strategy before it can be published.",
  BINDING_EVIDENCE_UNVERIFIABLE:
    "This strategy's governed-input record is incomplete, so we cannot confirm it still matches the campaign. Nothing indicates the campaign itself changed.",
  LEGACY_BINDING_UNVERIFIABLE:
    "This strategy's governed-input record is incomplete, so we cannot confirm it still matches the campaign. Nothing indicates the campaign itself changed.",
  CAMPAIGN_APPROVAL_STATE_CONTRADICTORY:
    "This strategy has approval evidence that the campaign's current status does not reflect. An operator needs to reconcile the two before approving again.",
});

const GENERIC_BLOCKING_REASON_DETAIL =
  "This strategy cannot be approved yet. Review its governance evidence for the outstanding prerequisite.";

/**
 * Map an internal reason code to operator-facing copy.
 *
 * A code this build does not recognise NEVER reaches the page as itself. The
 * raw code stays available on the projection (`blockingReason`) for logs and
 * diagnostics.
 */
export const describeStrategyBlockingReason = (blockingReason) => {
  const code = blockingReason ? String(blockingReason).trim().toUpperCase() : null;
  if (!code) return null;
  return {
    code,
    label: STRATEGY_BLOCKING_REASON_LABELS[code] || "APPROVAL BLOCKED",
    detail: STRATEGY_BLOCKING_REASON_DETAILS[code] || GENERIC_BLOCKING_REASON_DETAIL,
  };
};

// One banner. Never a stack of notices saying the same thing three ways.
export const STRATEGY_STATUS_BANNERS = Object.freeze({
  CURRENT_REVIEWABLE: {
    label: "AWAITING APPROVAL",
    tone: "review",
    detail:
      "This is the current governed strategy for this campaign. It is bound to the campaign's governed inputs and ready for your review.",
  },
  HISTORICAL_BOUND: {
    label: "HISTORICAL STRATEGY · REVISION REQUIRED",
    tone: "historical",
    detail:
      "The governed inputs changed after this strategy was generated, so it no longer describes the current campaign. It is shown for internal reference and cannot be approved as current.",
  },
  HISTORICAL_UNBOUND: {
    label: "HISTORICAL STRATEGY · LEGACY UNBOUND",
    tone: "historical",
    detail:
      "This strategy predates the current governed allocation and operation system, so nothing binds it to the campaign's present state. It is available for reference only.",
  },
  UNAVAILABLE: {
    label: "NO STRATEGY ON FILE",
    tone: "blocked",
    detail: "No persisted strategy exists for this campaign yet.",
  },
});

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isSha256 = (value) => /^[a-f0-9]{64}$/i.test(String(value || "").trim());

// The fail-closed floor. Every mutation verdict is false, and nothing renders.
const DENIED = Object.freeze({
  projectionPresent: false,
  projectionValid: false,
  canViewStrategy: false,
  displayArtifactAvailable: false,
  canApprove: false,
  canPublish: false,
  canReleaseMicrosite: false,
  requiresRegeneration: false,
  displayArtifact: null,
  displayArtifactStatus: STRATEGY_ARTIFACT_STATUS.UNAVAILABLE,
  displayArtifactAudience: null,
  displayArtifactIsCurrent: false,
  displayArtifactProvenance: null,
  approvalRevisionKey: null,
  pendingRevision: null,
  latestAuthorizedRevision: null,
  publicRevision: null,
  blockingReason: null,
  blockingReasonLabel: null,
  nextAction: null,
  banner: STRATEGY_STATUS_BANNERS.UNAVAILABLE,
  isHistorical: false,
  latestOperation: null,
});

/**
 * Read the canonical projection off a microsite payload.
 *
 * Returns the fail-closed floor for anything it cannot positively verify:
 * absent projection, non-object projection, unknown artifact status, or a
 * canViewStrategy that is not literally `true`.
 */
export const resolveStrategyVisibility = (micrositePayload = null) => {
  const projection =
    micrositePayload?.strategyVisibility ??
    micrositePayload?.campaign?.strategyVisibility ??
    null;

  if (!isPlainObject(projection)) return DENIED;

  const status = String(projection.displayArtifactStatus || "").trim().toUpperCase();
  if (!KNOWN_STATUSES.has(status)) {
    // An artifact status this build does not understand is an availability
    // problem, not a licence to render or to act.
    return { ...DENIED, projectionPresent: true };
  }

  // Strictly boolean. A truthy-but-not-true value is malformed evidence.
  const canViewStrategy = projection.canViewStrategy === true;
  const canApprove = projection.canApprove === true;
  const canPublish = projection.canPublish === true;
  const canReleaseMicrosite = projection.canReleaseMicrosite === true;

  const displayArtifact = isPlainObject(projection.displayArtifact)
    ? projection.displayArtifact
    : null;

  const blockingReason = projection.blockingReason
    ? String(projection.blockingReason).trim().toUpperCase()
    : null;

  // A revision key is only usable if it is a real immutable identity.
  const rawKey = String(projection.approvalRevisionKey || "").trim();
  const approvalRevisionKey = isSha256(rawKey) ? rawKey : null;

  const isHistorical =
    status === STRATEGY_ARTIFACT_STATUS.HISTORICAL_BOUND ||
    status === STRATEGY_ARTIFACT_STATUS.HISTORICAL_UNBOUND;

  return {
    projectionPresent: true,
    projectionValid: true,
    // Viewing is the BACKEND's verdict, verbatim. It is never inferred from a
    // mutation verdict — and never from whether an artifact happens to be
    // attached: the projection carries displayArtifact for the superadmin
    // preview, while a client-review response delivers the same content in the
    // ordinary payload body. Requiring the attachment here denied Cortex a
    // strategy the backend had explicitly authorised it to read.
    canViewStrategy,
    displayArtifactAvailable: Boolean(displayArtifact),
    canApprove,
    canPublish,
    canReleaseMicrosite,
    requiresRegeneration: projection.requiresRegeneration === true,
    displayArtifact,
    displayArtifactStatus: status,
    // The audience the BACKEND stamped on this projection, from the
    // authenticated server-side identity. A consumer must refuse a projection
    // addressed to a different audience rather than widen its own access.
    displayArtifactAudience:
      String(projection.displayArtifactAudience || "").trim().toUpperCase() || null,
    displayArtifactIsCurrent: projection.displayArtifactIsCurrent === true,
    displayArtifactProvenance: isPlainObject(projection.displayArtifactProvenance)
      ? projection.displayArtifactProvenance
      : null,
    approvalRevisionKey,
    pendingRevision: isPlainObject(projection.pendingRevision)
      ? projection.pendingRevision
      : null,
    latestAuthorizedRevision: isPlainObject(projection.latestAuthorizedRevision)
      ? projection.latestAuthorizedRevision
      : null,
    publicRevision: isPlainObject(projection.publicRevision)
      ? projection.publicRevision
      : null,
    blockingReason,
    /*
     * NEVER the raw code.
     *
     * This used to fall back to `blockingReason.replace(/_/g, " ")`, so any
     * code missing from the label table was printed verbatim — which is how
     * "BLOCKED BY CAMPAIGN APPROVAL STATE CONTRADICTORY" reached an operator's
     * screen. Unknown codes now get a generic operator-facing label, and the
     * raw code remains on `blockingReason` for diagnostics.
     */
    blockingReasonLabel: blockingReason
      ? describeStrategyBlockingReason(blockingReason).label
      : null,
    blockingReasonDetail: blockingReason
      ? describeStrategyBlockingReason(blockingReason).detail
      : null,
    nextAction: projection.nextAction ?? null,
    banner: STRATEGY_STATUS_BANNERS[status] || STRATEGY_STATUS_BANNERS.UNAVAILABLE,
    isHistorical,
    latestOperation:
      isPlainObject(projection.displayArtifactProvenance) &&
      isPlainObject(projection.displayArtifactProvenance.latestOperation)
        ? projection.displayArtifactProvenance.latestOperation
        : null,
  };
};

/**
 * Is this revision genuinely awaiting a human decision right now?
 *
 * Every condition must hold. Historical, stale, allocation-blocked and
 * terminal-failure artifacts are excluded by construction, because they can
 * never satisfy CURRENT_REVIEWABLE + canApprove + a valid revision key.
 */
export const isActionableApprovalRevision = (visibility = null) =>
  Boolean(
    visibility &&
      visibility.projectionValid === true &&
      visibility.displayArtifactStatus === STRATEGY_ARTIFACT_STATUS.CURRENT_REVIEWABLE &&
      visibility.canViewStrategy === true &&
      visibility.canApprove === true &&
      visibility.displayArtifactIsCurrent === true &&
      isSha256(visibility.approvalRevisionKey)
  );

// A settled operation is never in flight. UPDATING… is a lie about a
// FAILED_TERMINAL row, whatever its attempt count says.
export const isOperationSettled = (operation = null) =>
  Boolean(operation && operation.settled === true);

export const resolveOperationDisplayState = (operation = null) => {
  if (!operation) return null;
  const status = String(operation.status || "").trim().toUpperCase();
  const settled = isOperationSettled(operation);
  return {
    status,
    settled,
    // Only a genuinely unsettled operation may ever read as in progress.
    label: settled ? `${status.replace(/_/g, " ")} · SETTLED` : "UPDATING…",
    isTerminalFailure: settled && status.startsWith("FAILED"),
    failureClass: operation.failureClass || null,
    reference: operation.id ? String(operation.id).slice(0, 8).toUpperCase() : null,
  };
};
